import React, { useEffect, useRef, useState } from "react";

export default function ImageUploader({
  images = [],
  setImages,
  error = "",
  setError = () => {},
  maxImages = 5,
  maxSizeMB = 1,
  label = "Images",
}) {
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);

  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  const remainingSlots = maxImages - images.length;
  const isFull = remainingSlots <= 0;

  /* Sync previews from images */
  useEffect(() => {
    const urls = images.map((file) =>
      typeof file === "string" ? file : URL.createObjectURL(file),
    );

    setPreviews(urls);

    return () => {
      urls.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [images]);

  /* Auto clear full error when slots available */
  useEffect(() => {
    if (remainingSlots > 0 && error?.includes("Maximum")) {
      setError("");
    }
  }, [remainingSlots, error, setError]);

  const handleSelectFiles = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    if (remainingSlots <= 0) {
      setError(`Maximum ${maxImages} images allowed.`);
      e.target.value = "";
      return;
    }

    const limitedFiles = files.slice(0, remainingSlots);

    const validFiles = [];

    for (const file of limitedFiles) {
      if (!allowedTypes.includes(file.type)) {
        setError("Only JPEG, JPG, PNG, WEBP images are allowed.");
        e.target.value = "";
        return;
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`Each image must be under ${maxSizeMB}MB.`);
        e.target.value = "";
        return;
      }

      validFiles.push(file);
    }

    setImages((prev) => [...prev, ...validFiles]);
    setError("");

    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const moveLeft = (index) => {
    if (index === 0) return;

    setImages((prev) => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };

  const moveRight = (index) => {
    if (index === images.length - 1) return;

    setImages((prev) => {
      const arr = [...prev];
      [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
      return arr;
    });
  };
  const getHelperMessage = () => {
    if (images.length === 0)
      return "Upload your first image. Products with photos sell faster.";

    if (images.length === 1)
      return "Great start. Add more angles of your image to improve buyer confidence.";

    if (images.length < maxImages)
      return "Nice! More images help customers decide faster.";

    return "Excellent! Maximum images added.";
  };
  return (
    <div className="card mb-4 shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{label}</h5>

          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={isFull}
            onClick={() => fileInputRef.current?.click()}
          >
            + Upload
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          disabled={isFull}
          onChange={handleSelectFiles}
        />

        <small
          className={`d-block mb-3 ${
            isFull ? "text-danger fw-semibold" : "text-muted"
          }`}
        >
          {isFull
            ? `Maximum ${maxImages} images reached`
            : `${images.length}/${maxImages} selected • ${remainingSlots} slot${
                remainingSlots > 1 ? "s" : ""
              } remaining`}
        </small>

        {error && <div className="alert alert-danger py-2">{error}</div>}

        <div className="d-flex flex-wrap gap-3">
          {previews.map((src, i) => (
            <div
              key={i}
              className="position-relative border rounded p-1 bg-white"
            >
              <img
                src={src}
                alt=""
                style={{
                  width: 110,
                  height: 110,
                  objectFit: "cover",
                }}
                className="rounded"
              />

              {i === 0 && (
                <span className="badge bg-primary position-absolute bottom-0 start-0">
                  Main
                </span>
              )}

              <button
                type="button"
                className="btn btn-sm btn-danger position-absolute top-0 end-0"
                onClick={() => removeImage(i)}
              >
                ×
              </button>

              <div className="d-flex gap-1 mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light w-100"
                  disabled={i === 0}
                  onClick={() => moveLeft(i)}
                >
                  ←
                </button>

                <button
                  type="button"
                  className="btn btn-sm btn-light w-100"
                  disabled={i === previews.length - 1}
                  onClick={() => moveRight(i)}
                >
                  →
                </button>
              </div>
            </div>
          ))}
        </div>
        <small className="text-muted d-block mt-2">{getHelperMessage()}</small>
        {/* 
        {!previews.length && (
          <div className="text-muted small mt-2">No images uploaded yet.</div>
        )} */}
      </div>
    </div>
  );
}
